import { createAsyncAtom } from '../lib';
import rpcHealthAtom, { RpcHealthStatus } from './rpcHealthAtom';

export type TransactionWaitStatus = {
  isWaiting: boolean;
  txHash: string | null;
  retryCount: number;
  maxRetries: number;
  startTime: number | null;
};

export type ConnectionStatus = {
  rpcHealth: RpcHealthStatus;
  transactionWait: TransactionWaitStatus;
  hasActiveIssues: boolean;
  issueMessages: string[];
};

const DEFAULT_TRANSACTION_WAIT_STATUS: TransactionWaitStatus = {
  isWaiting: false,
  txHash: null,
  retryCount: 0,
  maxRetries: 40,
  startTime: null,
};

let transactionWaitState: TransactionWaitStatus = DEFAULT_TRANSACTION_WAIT_STATUS;

/**
 * Atom that combines RPC health and transaction wait status
 * to provide a unified view of connection issues.
 */
const connectionStatusAtom = createAsyncAtom<ConnectionStatus>(async get => {
  const rpcHealth = await get(rpcHealthAtom);

  const issueMessages: string[] = [];

  if (!rpcHealth.isHealthy) {
    if (rpcHealth.consecutiveFailures >= 3) {
      issueMessages.push('Network connection issues detected. Some features may not work properly.');
    } else {
      issueMessages.push('Experiencing temporary network issues. Retrying...');
    }
  }

  if (transactionWaitState.isWaiting && transactionWaitState.retryCount > 10) {
    issueMessages.push(
      `Transaction confirmation taking longer than expected (attempt ${transactionWaitState.retryCount}/${transactionWaitState.maxRetries}).`,
    );
  }

  if (transactionWaitState.isWaiting && transactionWaitState.retryCount > 20) {
    issueMessages.push(
      'Transaction may have failed or the network is congested. Consider refreshing if this persists.',
    );
  }

  return {
    rpcHealth,
    transactionWait: transactionWaitState,
    hasActiveIssues: issueMessages.length > 0,
    issueMessages,
  };
});

/**
 * Update the transaction wait status when waiting for a receipt.
 */
export function updateTransactionWaitStatus(
  isWaiting: boolean,
  txHash: string | null = null,
  retryCount: number = 0,
  maxRetries: number = 40,
): void {
  transactionWaitState = {
    isWaiting,
    txHash,
    retryCount,
    maxRetries,
    startTime: isWaiting && !transactionWaitState.isWaiting ? Date.now() : transactionWaitState.startTime,
  };

  // Trigger a refresh of the connection status
  const currentStatus = connectionStatusAtom.getResolvedValue();
  if (currentStatus) {
    connectionStatusAtom.setValue({
      ...currentStatus,
      transactionWait: transactionWaitState,
      hasActiveIssues:
        !currentStatus.rpcHealth.isHealthy ||
        (transactionWaitState.isWaiting && transactionWaitState.retryCount > 10),
      issueMessages: buildIssueMessages(currentStatus.rpcHealth, transactionWaitState),
    });
  }
}

/**
 * Reset transaction wait status (e.g., when transaction completes or fails).
 */
export function resetTransactionWaitStatus(): void {
  transactionWaitState = DEFAULT_TRANSACTION_WAIT_STATUS;
  const currentStatus = connectionStatusAtom.getResolvedValue();
  if (currentStatus) {
    connectionStatusAtom.setValue({
      ...currentStatus,
      transactionWait: transactionWaitState,
      hasActiveIssues: !currentStatus.rpcHealth.isHealthy,
      issueMessages: buildIssueMessages(currentStatus.rpcHealth, transactionWaitState),
    });
  }
}

function buildIssueMessages(rpcHealth: RpcHealthStatus, txWait: TransactionWaitStatus): string[] {
  const messages: string[] = [];

  if (!rpcHealth.isHealthy) {
    if (rpcHealth.consecutiveFailures >= 3) {
      messages.push('Network connection issues detected. Some features may not work properly.');
    } else {
      messages.push('Experiencing temporary network issues. Retrying...');
    }
  }

  if (txWait.isWaiting && txWait.retryCount > 10) {
    messages.push(
      `Transaction confirmation taking longer than expected (attempt ${txWait.retryCount}/${txWait.maxRetries}).`,
    );
  }

  if (txWait.isWaiting && txWait.retryCount > 20) {
    messages.push('Transaction may have failed or the network is congested. Consider refreshing if this persists.');
  }

  return messages;
}

export default connectionStatusAtom;
